/**
 * DOCUMENTACIÓN DE LA PLATAFORMA NARA
 *
 * Este archivo contiene toda la documentación de usuario organizada por módulos.
 * Cada vez que se agregue o modifique una funcionalidad, actualizar la sección correspondiente.
 *
 * Última actualización: 2026-01-12
 */

export const DOCUMENTACION = {
  // Información general de la plataforma
  general: {
    titulo: "👋 Bienvenido a NARA",
    descripcion: "NARA es una plataforma médica integral que combina expedientes clínicos electrónicos, chatbots de WhatsApp para agendamiento automático, y herramientas de productividad para profesionales de la salud.",
    version: "1.0.0"
  },

  // Módulos de la plataforma
  modulos: [
    {
      id: "dashboard",
      nombre: "🏠 Dashboard",
      icono: "Dashboard",
      color: "#1e3a5f",
      descripcion: "Panel principal con acceso a todas las aplicaciones de la plataforma.",
      secciones: [
        {
          titulo: "🖥️ Vista General",
          contenido: `El Dashboard es tu punto de entrada a NARA. Desde aquí puedes acceder a todas las aplicaciones disponibles mediante tarjetas interactivas.

**📌 Elementos del Dashboard:**
• **Header**: Muestra el logo, centro de notificaciones 🔔, selector de modo oscuro/claro 🌙, y tu avatar con menú de usuario.
• **Tarjetas de aplicaciones**: Cada tarjeta representa un módulo. Las tarjetas con color vibrante están disponibles ✅, las grises están próximamente 🔜.
• **Barra de búsqueda**: 🔍 Filtra las aplicaciones por nombre.`
        },
        {
          titulo: "👤 Perfil de Usuario",
          contenido: `Haz clic en tu avatar (esquina superior derecha) para acceder al menú de usuario.

**⚙️ Opciones disponibles:**
• **Mi Perfil**: Ver información de tu cuenta, plan de suscripción 💳 y configurar tu agenda de disponibilidad.
• **Cerrar Sesión**: 🚪 Salir de la plataforma de forma segura.

**📅 Configuración de Agenda:**
En "Mi Perfil" puedes configurar:
• 📆 Días de la semana que atiendes
• ⏰ Hora de inicio y fin de consultas
• ⏱️ Duración de cada cita (15, 20, 30, 45 o 60 minutos)

Esta configuración se usa para el agendamiento automático via WhatsApp.`
        },
        {
          titulo: "🔔 Centro de Notificaciones",
          contenido: `El icono de campana en el header muestra tus notificaciones.

**📬 Tipos de notificaciones:**
• ⏰ **Recordatorios de citas**: 24 horas y 2 horas antes de cada cita
• 🆕 **Citas nuevas**: Cuando un paciente agenda una cita
• ✅ **Citas confirmadas**: Cuando el paciente confirma asistencia
• ❌ **Citas canceladas**: Cuando se cancela una cita

**🖱️ Acciones:**
• Clic en una notificación para marcarla como leída
• "Marcar todas como leídas" para limpiar el contador
• Las notificaciones no leídas aparecen con fondo azul claro 🔵`
        }
      ]
    },
    {
      id: "emr",
      nombre: "🏥 Expediente Médico (EMR)",
      icono: "LocalHospital",
      color: "#10b981",
      descripcion: "Gestión completa de expedientes clínicos electrónicos con asistente de IA.",
      secciones: [
        {
          titulo: "📋 Lista de Pacientes",
          contenido: `Al entrar al módulo EMR verás la lista de tus pacientes.

**🔧 Funcionalidades:**
• 🔍 **Buscar paciente**: Escribe nombre o cédula en la barra de búsqueda
• ➕ **Agregar paciente**: Botón "Nuevo Paciente" para registrar uno nuevo
• 👁️ **Ver expediente**: Clic en cualquier paciente para abrir su expediente

**⚠️ Importante:** Solo verás los pacientes asignados a ti como médico tratante, independientemente de tu rol en el sistema.`
        },
        {
          titulo: "📁 Expediente del Paciente",
          contenido: `El expediente de cada paciente tiene varias pestañas:

**👤 Información General:**
• 📝 Datos demográficos (nombre, cédula, fecha nacimiento, género)
• 📞 Información de contacto (teléfono, email, dirección)
• 🆘 Contacto de emergencia

**📜 Historia Clínica:**
• 📖 Antecedentes personales y familiares
• ⚠️ Alergias
• 💊 Medicamentos actuales
• 📅 Historial de consultas

**🩺 Consultas:**
• 📝 Registro de cada visita médica
• 🔍 Motivo de consulta, examen físico, diagnóstico, plan
• 📋 Notas evolutivas

**📎 Documentos:**
• 📄 Archivos adjuntos (exámenes, imágenes, etc.)
• 📑 PDFs y documentos escaneados`
        },
        {
          titulo: "🤖 Asistente de IA",
          contenido: `El EMR incluye un asistente de IA que te ayuda durante la consulta.

**🚀 Cómo usarlo:**
1. Abre el expediente del paciente
2. Busca el icono de chat/asistente 💬
3. Escribe tu pregunta o solicitud

**💡 Ejemplos de uso:**
• "Resume los antecedentes de este paciente" 📋
• "¿Qué medicamentos está tomando actualmente?" 💊
• "Sugiere diagnósticos diferenciales para [síntomas]" 🔍
• "Genera una nota de consulta basada en: [descripción]" 📝

El asistente tiene contexto del paciente actual y puede ayudarte a documentar más rápido ⚡.`
        }
      ]
    },
    {
      id: "agenda",
      nombre: "📅 Agenda de Citas",
      icono: "CalendarMonth",
      color: "#0ea5e9",
      descripcion: "Visualización y gestión de citas agendadas automáticamente por los chatbots.",
      secciones: [
        {
          titulo: "📆 Vista de Calendario",
          contenido: `La agenda muestra tus citas en formato de calendario.

**👁️ Vistas disponibles:**
• **Día**: Ver todas las citas de un día específico 📍
• **Semana**: Vista semanal con franjas horarias 📊
• **Mes**: Vista mensual con indicadores de citas 🗓️

**🧭 Navegación:**
• ◀️ ▶️ Usa las flechas para moverte entre fechas
• 📍 Clic en "Hoy" para volver a la fecha actual
• 🖱️ Clic en una fecha del mes para ir directamente a ella`
        },
        {
          titulo: "✏️ Gestión de Citas",
          contenido: `**🏷️ Estados de las citas:**
• 🟡 **Pendiente**: Cita agendada, esperando confirmación
• 🟢 **Confirmada**: Paciente confirmó asistencia
• ✅ **Completada**: Cita realizada
• 🔴 **Cancelada**: Cita cancelada
• ⚫ **No asistió**: Paciente no se presentó

**🔧 Acciones sobre una cita:**
• 👁️ Clic en la cita para ver detalles
• 🔄 Cambiar estado de la cita
• 📝 Agregar notas
• ❌ Cancelar o reprogramar

**⚠️ Importante:** Solo verás las citas donde tú eres el médico asignado.`
        },
        {
          titulo: "📊 Estadísticas",
          contenido: `La agenda incluye estadísticas de tus citas.

**📈 Métricas disponibles:**
• 📊 Total de citas del período
• ✅ Citas completadas vs pendientes
• 📉 Tasa de asistencia
• 📅 Citas por día de la semana

Estas estadísticas te ayudan a entender tus patrones de atención y optimizar tu disponibilidad 💡.`
        }
      ]
    },
    {
      id: "constructor",
      nombre: "🔧 Constructor de Flujos",
      icono: "AccountTree",
      color: "#6366f1",
      descripcion: "Diseña flujos conversacionales para tus chatbots de WhatsApp.",
      secciones: [
        {
          titulo: "❓ ¿Qué son los Flujos?",
          contenido: `Los flujos conversacionales definen cómo tu chatbot interactúa con los pacientes por WhatsApp 💬.

**🎯 Usos comunes:**
• 📅 Agendamiento automático de citas
• ✅ Confirmación de citas programadas
• ❓ Respuestas a preguntas frecuentes
• 📝 Recopilación de información del paciente
• ⏰ Envío de recordatorios

Cada flujo es una secuencia de pasos que guían la conversación con el paciente 🗺️.`
        },
        {
          titulo: "➕ Crear un Flujo",
          contenido: `**🚀 Para crear un nuevo flujo:**
1. Entra al Constructor de Flujos
2. Clic en "Nuevo Flujo" o usa el botón ➕
3. Dale un nombre descriptivo al flujo
4. Arrastra y conecta los nodos

**🧩 Tipos de nodos:**
• 💬 **Mensaje**: Envía un mensaje al paciente
• ❓ **Pregunta**: Espera una respuesta del paciente
• 🔀 **Condición**: Bifurca según la respuesta
• ⚡ **Acción**: Ejecuta una acción (agendar cita, guardar dato, etc.)
• 🏁 **Fin**: Termina el flujo

**🔗 Conexiones:**
Arrastra desde el punto de salida de un nodo hasta el punto de entrada de otro para conectarlos.`
        },
        {
          titulo: "💾 Guardar y Cargar Flujos",
          contenido: `**💾 Guardar flujo:**
• Clic en el icono de guardar o Ctrl+S ⌨️
• El flujo se guarda con su nombre y todos los nodos

**📂 Cargar flujo guardado:**
• Clic en el icono de carpeta 📁
• Selecciona el flujo de la lista
• El flujo se carga en el canvas

**🟢 Activar flujo:**
• Un flujo debe estar "activo" para que el chatbot lo use
• Solo puede haber un flujo activo a la vez ☝️
• Activa el flujo desde la lista de flujos guardados`
        },
        {
          titulo: "⭐ Nodos Especiales",
          contenido: `**📅 Nodo de Agendamiento:**
Este nodo permite al paciente agendar una cita automáticamente.
• 📆 Muestra los horarios disponibles según tu configuración de agenda
• 🖱️ El paciente selecciona fecha y hora
• ✅ La cita se crea automáticamente en el sistema

**🤖 Nodo de IA:**
Permite que la IA responda preguntas del paciente.
• 💡 Útil para preguntas fuera del flujo predefinido
• 🧠 La IA tiene contexto de la conversación

**👤 Nodo de Transferencia:**
Transfiere la conversación a atención humana cuando es necesario 🆘.`
        },
        {
          titulo: "✨ Asistente Inteligente con IA",
          contenido: `El Constructor incluye un **Asistente Inteligente** 🤖 potenciado por IA que te ayuda a crear flujos conversacionales de forma rápida y sencilla.

**🚀 Cómo acceder:**
• Busca el botón flotante con el logo de NARA en la esquina inferior izquierda del canvas
• Haz clic para abrir el diálogo del asistente

**📑 Pestañas disponibles:**

**1. 💬 Chat con IA:**
Escribe en lenguaje natural lo que necesitas y la IA generará el flujo por ti.

*💡 Ejemplos de prompts:*
• "Crea un flujo para agendar citas médicas que pida nombre, motivo de consulta y permita seleccionar fecha" 📅
• "Un flujo para confirmar citas donde el paciente pueda confirmar, reagendar o cancelar" ✅
• "Flujo de triaje que capture síntomas y determine nivel de urgencia" 🏥

**2. 📚 Ejemplos de Prompts:**
Biblioteca de prompts predefinidos organizados por categorías:
• 📅 **Agendamiento**: Flujos de citas, confirmaciones, recordatorios
• 🏥 **Atención al Paciente**: Triaje, registro, consulta de horarios
• 👨‍⚕️ **Especialidades**: Dermatología, pediatría, odontología
• 💬 **Soporte**: FAQ, escalación, encuestas

Cada ejemplo tiene botones para:
• 📋 **Copiar**: Copia el prompt al portapapeles
• ▶️ **Usar**: Carga el prompt directamente en el chat

**🔄 Flujo de trabajo:**
1. Describe el flujo que necesitas (o usa un ejemplo)
2. La IA genera los nodos propuestos 🧩
3. Revisa el preview de nodos 👁️
4. Clic en "✅ Agregar al canvas" para insertarlos
5. Los nodos aparecen conectados automáticamente en tu canvas 🔗
6. Ajusta y personaliza según necesites ✏️`
        },
        {
          titulo: "📱 Simulador de Celular (WhatsApp)",
          contenido: `El Constructor incluye un **Simulador de Celular** que te permite probar tus flujos conversacionales en tiempo real, simulando exactamente cómo se verá la conversación en WhatsApp.

**🚀 Cómo acceder:**
• El simulador aparece automáticamente en la barra lateral derecha cuando estás en **modo WhatsApp** (no en modo llamada)
• Busca el icono de celular con diseño estilo iPhone

**📱 Características del Simulador:**
• **Diseño iPhone**: Marco realista con Dynamic Island, barra de estado (hora, 5G, batería) y barra de home
• **Interfaz WhatsApp**: Header verde con avatar, nombre del contacto y estado "en línea"
• **Burbujas de chat**: Mensajes estilizados con "colitas" como en WhatsApp real
• **Indicador de escritura**: Animación de "escribiendo..." cuando el bot procesa

**⚙️ Controles del Simulador:**

**1. Toggle "Cliente Registrado" / "Cliente Nuevo":**
• 👤 **Cliente Registrado**: Simula que el número ya existe en la base de datos (para probar flujos de clientes existentes)
• 🆕 **Cliente Nuevo**: Simula que es un número nuevo sin registro previo
• Este toggle es esencial para probar nodos de verificación de cliente

**2. Simulación de Multimedia:**
• 🎤 **Botón de micrófono**: Mantén presionado para simular envío de nota de voz
• 📷 **Botón de cámara**: Simula envío de imagen

**3. Campo de Texto:**
• ✍️ Escribe tu mensaje y presiona el botón de enviar (aparece al escribir)
• Los mensajes se procesan a través del backend real del chatbot

**🔄 Flujo de Prueba:**
1. Asegúrate de tener un flujo con nodos conectados
2. Escribe un mensaje en el simulador (por ejemplo: "Hola")
3. El mensaje se envía al backend vía \`/simulador/mensaje\`
4. El bot responde según el flujo activo
5. Continúa la conversación para probar todo el flujo

**💡 Tips:**
• 🔄 Usa el toggle de cliente para probar ambos escenarios (nuevo vs existente)
• 📋 El simulador mantiene el historial de la conversación
• ⚡ Las respuestas son en tiempo real, procesadas por el mismo motor del chatbot
• 🧪 Prueba diferentes respuestas para verificar las condiciones del flujo

**⚠️ Nota:** El simulador usa un número ficticio (+52 1 555 123 4567) que no afecta conversaciones reales de WhatsApp.`
        },
        {
          titulo: "🔧 Barra de Herramientas",
          contenido: `La barra de herramientas del Constructor está ubicada en la parte superior y proporciona acceso rápido a todas las funciones.

**🏠 Navegación:**
• **Inicio**: Vuelve al Dashboard

**↩️ Edición:**
• **Deshacer**: Revierte la última acción (Ctrl+Z)
• **Rehacer**: Aplica nuevamente la acción deshecha (Ctrl+Y)

**💾 Gestión de Flujos:**
• **Guardar**: Guarda el flujo actual en la base de datos
• **AI Agent**: Configura el agente de IA híbrido
• **Cargar flujo**: Abre un flujo guardado previamente
• **Nuevo flujo**: Crea un flujo desde cero

**📂 Menús desplegables:**
• **Administrar** (morado): Acceso a plantillas, variables, contextos y más
• **Apps** (azul): Navegación rápida a otras aplicaciones
• **Herramientas** (verde): Validar flujo, estadísticas y atajos

**🔍 Zoom y Vista:**
• **Acercar/Alejar**: Controla el nivel de zoom
• **Centrar**: Ajusta la vista para mostrar todo el flujo
• **Vista 100%**: Restaura el zoom al 100%

**💡 Tip:** En pantallas medianas (<1920px), la barra se compacta automáticamente para aprovechar mejor el espacio.`
        },
        {
          titulo: "📂 Menú Administrar",
          contenido: `El menú **Administrar** (botón morado) consolida todas las opciones de configuración y administración del Constructor.

**📋 Opciones disponibles:**

**1. Plantillas Globales:**
Gestiona plantillas de flujos reutilizables que puedes insertar en cualquier flujo.

**2. Variables Globales:**
Define variables que pueden usarse en todos los flujos para personalizar mensajes.

**3. Contextos:**
Administra los contextos disponibles para organizar y categorizar tus flujos.

**4. Tipos de Mensaje:**
Configura los diferentes tipos de mensajes que puede enviar el chatbot.

**5. Mensajes Predefinidos:**
Biblioteca de mensajes listos para usar en tus flujos.

**6. Preguntas Predefinidas:**
Gestiona preguntas frecuentes y sus respuestas automáticas.

**⚠️ Nota:** Algunas opciones solo están disponibles para usuarios administradores.`
        },
        {
          titulo: "📱 Menú Apps",
          contenido: `El menú **Apps** (botón azul) te permite navegar rápidamente entre las diferentes aplicaciones de NARA sin volver al Dashboard.

**🚀 Aplicaciones disponibles:**

• **🏥 Expediente Médico**: Accede al módulo EMR para gestionar historias clínicas
• **📅 Agenda de Citas**: Ve y gestiona las citas programadas
• **📊 Analíticas**: Consulta métricas y estadísticas de uso
• **💬 WhatsApp Manager**: Gestiona conexiones y conversaciones de WhatsApp
• **🏠 Volver al Dashboard**: Regresa al panel principal

**💡 Tip:** Usa este menú para cambiar entre módulos sin perder el contexto de trabajo actual.`
        },
        {
          titulo: "🛠️ Menú Herramientas",
          contenido: `El menú **Herramientas** (botón verde) proporciona utilidades para optimizar tu trabajo con flujos.

**✅ Validar Flujo:**
Analiza el flujo actual y detecta posibles problemas:
• **Errores** (rojo): Problemas que deben corregirse
  - Nodos de pregunta sin variable asignada
  - Nodos de opciones sin opciones definidas
• **Advertencias** (amarillo): Posibles mejoras
  - Nodos sin conexiones de entrada
  - Nodos sin conexiones de salida

**📊 Estadísticas del Flujo:**
Muestra información detallada sobre el flujo actual:
• Total de nodos y conexiones
• Conteo por tipo de nodo
• Lista de variables definidas

**🔍 Buscar Nodos:**
Busca un nodo por texto y centra la vista en él:
• Busca por etiqueta del nodo
• Busca por nombre de variable
• El canvas hace zoom automático al nodo encontrado

**⌨️ Atajos de Teclado:**
Muestra una guía visual de todos los atajos disponibles en el Constructor.`
        }
      ]
    },
    {
      id: "calculadoras",
      nombre: "🧮 Calculadoras Médicas",
      icono: "Calculate",
      color: "#f97316",
      descripcion: "Herramientas de cálculo médico para uso en consulta.",
      secciones: [
        {
          titulo: "📊 Calculadoras Disponibles",
          contenido: `NARA incluye 9 calculadoras médicas de uso frecuente:

**🔬 Generales:**
• 📏 **IMC**: Índice de Masa Corporal con interpretación
• 📐 **Superficie Corporal**: Fórmula de Mosteller
• 🩸 **Clearance de Creatinina**: Cockcroft-Gault

**👶 Pediatría:**
• 💊 **Dosis Pediátrica**: Cálculo por peso
• ⭐ **APGAR**: Puntuación del recién nacido

**🤰 Obstetricia:**
• 📅 **Fecha Probable de Parto**: Regla de Naegele

**🧠 Neurología:**
• 📋 **Escala de Glasgow**: Nivel de consciencia

**❤️ Cardiología/Urgencias:**
• 🫁 **Score de Wells**: Probabilidad de TEP
• 💓 **Riesgo Cardiovascular**: Framingham simplificado`
        },
        {
          titulo: "📖 Cómo Usar",
          contenido: `**🚀 Para acceder a las calculadoras:**
1. Desde el Dashboard, clic en "Calculadoras Médicas" 🧮
2. Se abre un diálogo con todas las calculadoras

**🔢 Para usar una calculadora:**
1. Selecciona la calculadora con los chips superiores
2. Ingresa los valores requeridos 📝
3. Clic en "Calcular" ▶️
4. El resultado aparece con interpretación clínica 📊

**💡 Tips:**
• 🔄 Puedes cambiar entre calculadoras sin cerrar el diálogo
• 🗑️ El botón "Limpiar" reinicia los campos
• 📋 Los resultados incluyen clasificación/interpretación cuando aplica`
        }
      ]
    },
    {
      id: "analytics",
      nombre: "📈 Analíticas",
      icono: "Analytics",
      color: "#8b5cf6",
      descripcion: "Métricas y estadísticas de uso de la plataforma.",
      secciones: [
        {
          titulo: "📊 Dashboard de Analíticas",
          contenido: `El módulo de analíticas te muestra métricas de tu práctica médica.

**🤖 Métricas de Chatbot:**
• 💬 Conversaciones totales
• 📨 Mensajes enviados/recibidos
• ✅ Tasa de resolución automática
• 👤 Conversaciones transferidas a humano

**📅 Métricas de Citas:**
• 📊 Citas agendadas por período
• 🔄 Fuente de agendamiento (chatbot vs manual)
• ✅ Tasa de asistencia
• ⏰ Horarios más solicitados

**👥 Métricas de Pacientes:**
• 🆕 Pacientes nuevos por período
• 🟢 Pacientes activos
• 📈 Frecuencia de visitas`
        }
      ]
    },
    {
      id: "whatsapp",
      nombre: "💬 WhatsApp Manager",
      icono: "WhatsApp",
      color: "#25D366",
      descripcion: "Gestión de conexiones y conversaciones de WhatsApp.",
      secciones: [
        {
          titulo: "🔗 Conexión de WhatsApp",
          contenido: `Para que los chatbots funcionen, necesitas conectar un número de WhatsApp 📱.

**🚀 Proceso de conexión:**
1. Ve a WhatsApp Manager
2. Clic en "Conectar nuevo número" ➕
3. Escanea el código QR con WhatsApp en tu teléfono 📷
4. La conexión se establece automáticamente ✅

**🚦 Estado de conexión:**
• 🟢 **Conectado** (verde): El número está activo y funcionando
• 🔴 **Desconectado** (rojo): Necesita reconexión
• 🟡 **Conectando** (amarillo): Proceso de conexión en curso

**⚠️ Importante:** El teléfono debe permanecer conectado a internet para mantener la sesión activa 📶.`
        },
        {
          titulo: "💬 Conversaciones",
          contenido: `Desde WhatsApp Manager puedes ver las conversaciones activas.

**📱 Panel de conversaciones:**
• 📋 Lista de chats activos a la izquierda
• 💬 Conversación seleccionada a la derecha
• 🤖/👤 Indicador de quién responde (bot o humano)

**🆘 Intervención manual:**
Puedes tomar el control de una conversación cuando:
• 👤 El paciente solicita hablar con una persona
• ❓ El bot no puede resolver la consulta
• ℹ️ Necesitas dar información específica

Para intervenir, abre la conversación y escribe tu mensaje ✍️.`
        }
      ]
    },
    {
      id: "consumo-tokens",
      nombre: "📊 Consumo de Servicios",
      icono: "DataUsage",
      color: "#06b6d4",
      descripcion: "Monitoreo del uso de IA, mensajería Twilio y embeddings por proveedor y modelo.",
      secciones: [
        {
          titulo: "📈 Vista General",
          contenido: `El módulo de Consumo de Servicios te permite monitorear el uso de todos los servicios de tu empresa: IA, mensajería Twilio y embeddings.

**🎯 ¿Qué se monitorea?**
• **Tokens de IA**: Unidades de procesamiento que las APIs (OpenAI, Anthropic) utilizan para facturar. Cada palabra consume aprox. 1-4 tokens.
• **Mensajes Twilio**: Cada mensaje de WhatsApp enviado a pacientes.
• **Embeddings**: Generación de vectores para memoria semántica.

**📊 Panel Principal:**
• **Total de Consumo**: Uso total del período seleccionado
• **Por Proveedor**: Desglose entre OpenAI, Anthropic, Twilio
• **Costo Estimado**: Valor en USD basado en precios de cada servicio

**🎨 Colores por proveedor:**
• 🟢 **OpenAI** (verde): GPT-4, GPT-3.5, Whisper
• 🟣 **Anthropic** (violeta): Claude 3 Opus, Sonnet, Haiku
• 🟡 **Google** (amarillo): Gemini Pro, Gemini Flash`
        },
        {
          titulo: "📅 Filtros de Período",
          contenido: `Puedes filtrar el consumo por año y mes.

**🗓️ Selectores disponibles:**
• **Año**: Selecciona el año a consultar (últimos 3 años disponibles)
• **Mes**: Filtra por un mes específico

**📊 Gráfico de Evolución:**
La gráfica de barras muestra el consumo mensual del año seleccionado, con barras apiladas por proveedor para visualizar la distribución del uso.

**🔄 Actualizar datos:**
Usa el botón de actualizar (🔄) para refrescar los datos en tiempo real.`
        },
        {
          titulo: "📋 Detalle por Modelo",
          contenido: `La tabla inferior muestra el detalle de consumo por modelo de IA.

**📊 Columnas de la tabla:**
• **Proveedor**: OpenAI, Anthropic o Google
• **Modelo**: Nombre específico del modelo (gpt-4o, claude-3-opus, etc.)
• **Input**: Tokens de entrada (prompts enviados)
• **Output**: Tokens de salida (respuestas generadas)
• **Total**: Suma de input + output
• **Costo**: Costo estimado en USD

**💡 Nota sobre costos:**
Los tokens de salida generalmente cuestan más que los de entrada. Por ejemplo:
• GPT-4o: $0.0025/1K input, $0.01/1K output
• Claude Opus: $0.015/1K input, $0.075/1K output

**🌙 Modo Oscuro:**
El módulo soporta modo oscuro/claro, sincronizado con tu preferencia del dashboard.`
        }
      ]
    }
  ],

  // Preguntas frecuentes
  faq: [
    {
      pregunta: "⏰ ¿Cómo configuro mi horario de atención?",
      respuesta: "Ve al Dashboard > clic en tu avatar > Mi Perfil > sección Agenda. Ahí puedes configurar días 📅, horarios ⏰ e intervalo de citas."
    },
    {
      pregunta: "👁️ ¿Por qué no veo todos los pacientes?",
      respuesta: "Solo puedes ver los pacientes donde eres el médico tratante asignado. Esto es por diseño para mantener la privacidad de los datos 🔒."
    },
    {
      pregunta: "🟢 ¿Cómo activo un flujo de chatbot?",
      respuesta: "Ve al Constructor de Flujos > abre el menú de flujos guardados 📂 > busca tu flujo > clic en 'Activar'. Solo puede haber un flujo activo a la vez ☝️."
    },
    {
      pregunta: "🤖 ¿Por qué el chatbot no responde?",
      respuesta: "Verifica: 1) El número de WhatsApp está conectado 🟢, 2) Hay un flujo activo ✅, 3) El flujo tiene un nodo de inicio configurado 🚀."
    },
    {
      pregunta: "🔔 ¿Cómo recibo notificaciones de citas nuevas?",
      respuesta: "Las notificaciones llegan automáticamente al centro de notificaciones (campana 🔔 en el header). También puedes recibir recordatorios de citas próximas ⏰."
    },
    {
      pregunta: "📱 ¿Los pacientes reciben recordatorios por WhatsApp?",
      respuesta: "Sí ✅, el sistema envía recordatorios automáticos 24 horas ⏰ y 2 horas antes de cada cita al número de WhatsApp del paciente."
    },
    {
      pregunta: "✨ ¿Cómo uso el Asistente Inteligente para crear flujos?",
      respuesta: "En el Constructor de Flujos, haz clic en el botón flotante con el logo de NARA 🤖 (esquina inferior izquierda). Puedes escribir en lenguaje natural lo que necesitas o usar los ejemplos predefinidos 📚. La IA generará los nodos y podrás agregarlos al canvas con un clic ✅."
    },
    {
      pregunta: "📱 ¿Cómo pruebo mi flujo de WhatsApp sin usar un número real?",
      respuesta: "Usa el Simulador de Celular 📱 en el Constructor de Flujos. Aparece en la barra lateral derecha cuando estás en modo WhatsApp. Tiene diseño de iPhone con interfaz WhatsApp real. Puedes simular si el cliente está registrado o es nuevo con el toggle, enviar mensajes de texto, notas de voz 🎤 y fotos 📷. Los mensajes se procesan con el mismo motor del chatbot real."
    }
  ],

  // Atajos de teclado
  atajos: [
    { tecla: "Ctrl + Z", accion: "↩️ Deshacer última acción", modulo: "Constructor" },
    { tecla: "Ctrl + Y", accion: "↪️ Rehacer acción", modulo: "Constructor" },
    { tecla: "Ctrl + S", accion: "💾 Guardar flujo", modulo: "Constructor" },
    { tecla: "Delete", accion: "🗑️ Eliminar nodo/conexión seleccionada", modulo: "Constructor" },
    { tecla: "Ctrl + A", accion: "✅ Seleccionar todos los nodos", modulo: "Constructor" },
    { tecla: "Ctrl + C", accion: "📋 Copiar nodo seleccionado", modulo: "Constructor" },
    { tecla: "Ctrl + V", accion: "📄 Pegar nodo copiado", modulo: "Constructor" },
    { tecla: "Scroll", accion: "🔍 Zoom in/out", modulo: "Constructor" },
    { tecla: "Shift + Click", accion: "☑️ Selección múltiple", modulo: "Constructor" },
    { tecla: "Escape", accion: "❌ Cerrar diálogo actual", modulo: "General" }
  ]
};

export default DOCUMENTACION;
